import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import Select from 'react-select';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import AuthStorage from '~/Utils/AuthStorage';
import { ESTADOS, caracteristicas } from '~/constant';
import {
  listarCidades,
  excluirCidade,
  listarRegioesAtendimento,
} from '~/Services/Service';
import ModalCidade from './ModalCidade';

const PAGE_SIZE = 50;

const ESTADOS_OPTIONS = [{ value: '', label: 'Todos os estados' }].concat(
  ESTADOS.map((e) => ({ value: e.value, label: e.value + ' — ' + e.label }))
);

const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const Cidades = () => {
  const [cidades, setCidades] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);

  const [filtroQ, setFiltroQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroRegiao, setFiltroRegiao] = useState('');
  const [regioes, setRegioes] = useState([]);

  const [ordenacao, setOrdenacao] = useState({ campo: 'descricao_asc' });

  const [showModal, setShowModal] = useState(false);
  const [cidadeSelecionada, setCidadeSelecionada] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const temPermissao = AuthStorage.temCaracteristicas([caracteristicas.ManutencaoCidades]);

  useEffect(() => {
    carregarRegioes();
  }, []);

  useEffect(() => {
    carregarCidades(1);
  }, [filtroQ, filtroEstado, filtroRegiao, ordenacao]);

  const carregarRegioes = async () => {
    try {
      const resp = await listarRegioesAtendimento();
      setRegioes(resp || []);
    } catch (e) {
      // silencioso — dropdown fica vazio
    }
  };

  const carregarCidades = useCallback(async (pg) => {
    setLoading(true);
    try {
      const resp = await listarCidades({
        q: filtroQ || undefined,
        estado: filtroEstado || undefined,
        codigoRegiao: filtroRegiao || undefined,
        page: pg,
        pageSize: PAGE_SIZE,
        ordenarPor: ordenacao.campo,
      });
      setCidades((resp && resp.itens) ? resp.itens : []);
      setTotal((resp && resp.total) ? resp.total : 0);
      setPagina(pg);
    } catch (e) {
      toast.error('Erro ao carregar cidades.');
    } finally {
      setLoading(false);
    }
  }, [filtroQ, filtroEstado, filtroRegiao, ordenacao]);

  const handleBuscar = () => {
    carregarCidades(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') carregarCidades(1);
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo + '_asc') return { campo: campo + '_desc' };
      return { campo: campo + '_asc' };
    });
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo === campo + '_asc') return <FaSortUp style={{ marginLeft: 4 }} />;
    if (ordenacao.campo === campo + '_desc') return <FaSortDown style={{ marginLeft: 4 }} />;
    return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
  };

  const handleNovo = () => {
    setCidadeSelecionada(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setCidadeSelecionada(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirCidade(item.codigo_cidade);
      toast.success('Cidade excluída com sucesso.');
      const novaPagina = cidades.length === 1 && pagina > 1 ? pagina - 1 : pagina;
      carregarCidades(novaPagina);
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao excluir cidade.';
      toast.error(mensagem);
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregarCidades(pagina);
  };

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    carregarCidades(pg);
  };

  const opsRegioes = [{ value: '', label: 'Todas as regiões' }].concat(
    regioes.map((r) => ({ value: r.codigo_regiao || r.codigo, label: r.descricao_regiao || r.descricao || '' }))
  );

  const regiaoValue = opsRegioes.find((o) => String(o.value) === String(filtroRegiao)) || opsRegioes[0];
  const estadoValue = ESTADOS_OPTIONS.find((o) => o.value === filtroEstado) || ESTADOS_OPTIONS[0];

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const pages = [];
    const maxBtn = 5;
    let start = Math.max(1, pagina - Math.floor(maxBtn / 2));
    const end = Math.min(totalPaginas, start + maxBtn - 1);
    if (end - start < maxBtn - 1) start = Math.max(1, end - maxBtn + 1);

    pages.push(
      <Pagination.First key="first" disabled={pagina === 1} onClick={() => { handlePagina(1); }} />,
      <Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => { handlePagina(pagina - 1); }} />
    );
    if (start > 1) pages.push(<Pagination.Ellipsis key="ell-start" disabled />);
    for (let i = start; i <= end; i++) {
      const pg = i;
      pages.push(
        <Pagination.Item key={pg} active={pg === pagina} onClick={() => { handlePagina(pg); }}>
          {pg}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) pages.push(<Pagination.Ellipsis key="ell-end" disabled />);
    pages.push(
      <Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => { handlePagina(pagina + 1); }} />,
      <Pagination.Last key="last" disabled={pagina === totalPaginas} onClick={() => { handlePagina(totalPaginas); }} />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {Math.min((pagina - 1) * PAGE_SIZE + 1, total)} até{' '}
          {Math.min(pagina * PAGE_SIZE, total)} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {pages}
        </Pagination>
      </div>
    );
  };

  if (loading && cidades.length === 0) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Cidades" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col md={4} className="mb-2 mb-md-0">
              <Form.Label as="small" className="mb-1 font-weight-bold">Busca</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Código ou descrição..."
                  value={filtroQ}
                  onChange={(e) => { setFiltroQ(e.target.value); }}
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label as="small" className="mb-1 font-weight-bold">Estado</Form.Label>
              <Select
                options={ESTADOS_OPTIONS}
                value={estadoValue}
                onChange={(opt) => { setFiltroEstado(opt ? opt.value : ''); }}
                styles={selectStylesSm}
                placeholder="Todos os estados"
              />
            </Col>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label as="small" className="mb-1 font-weight-bold">Região</Form.Label>
              <Select
                options={opsRegioes}
                value={regiaoValue}
                onChange={(opt) => { setFiltroRegiao(opt ? opt.value : ''); }}
                styles={selectStylesSm}
                placeholder="Todas as regiões"
              />
            </Col>
            <Col md={2} className="text-right">
              <Button
                size="sm"
                variant="primary"
                onClick={handleNovo}
                disabled={!temPermissao}
                title={!temPermissao ? 'Sem permissão' : ''}
              >
                <FaPlus className="mr-1" /> Nova Cidade
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Cidades
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({total} {total === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
          {loading && <small style={{ opacity: 0.8 }}>Carregando...</small>}
        </Card.Header>
        <Card.Body className="p-0">
          {cidades.length === 0 && !loading ? (
            <p className="text-muted text-center my-4">Nenhuma cidade encontrada.</p>
          ) : (
            <>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={Object.assign({ width: '80px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('codigo'); }}
                      >
                        Código{iconeOrdenacao('codigo')}
                      </th>
                      <th
                        style={thSortStyle}
                        onClick={() => { handleOrdenacao('descricao'); }}
                      >
                        Descrição{iconeOrdenacao('descricao')}
                      </th>
                      <th
                        style={Object.assign({ width: '70px' }, thSortStyle)}
                        onClick={() => { handleOrdenacao('estado'); }}
                      >
                        Estado{iconeOrdenacao('estado')}
                      </th>
                      <th>Região</th>
                      <th>País</th>
                      <th style={{ width: '60px' }}>DDD</th>
                      <th style={{ width: '130px' }}>Data Alteração</th>
                      <th style={{ width: '90px', textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cidades.map((item) => {
                      return (
                        <tr key={`${item.codigo_cidade}-${item.estado}`}>
                          <td>{item.codigo_cidade}</td>
                          <td>{item.descricao_cidade}</td>
                          <td>{item.estado}</td>
                          <td>{item.descricao_regiao || '—'}</td>
                          <td>{item.descricao_pais || '—'}</td>
                          <td>{item.ddd || '—'}</td>
                          <td>
                            {item.dt_alteracao
                              ? moment(item.dt_alteracao).format('DD/MM/YYYY HH:mm')
                              : '—'}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              title="Editar"
                              className="mr-1"
                              disabled={!temPermissao}
                              onClick={() => { handleEditar(item); }}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              title="Excluir"
                              disabled={!temPermissao}
                              onClick={() => { handleExcluir(item); }}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          confirmacao.item
            ? `Tem certeza que deseja excluir a cidade "${confirmacao.item.descricao_cidade}" (Código ${confirmacao.item.codigo_cidade})?`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalCidade
        show={showModal}
        onHide={() => { setShowModal(false); }}
        cidade={cidadeSelecionada}
        onSalvar={handleSalvar}
      />
    </Container>
  );
};

export default Cidades;
